import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { PagedData } from '@app/modules/lookup/models/paged-data.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EnvironmentService } from "@app/core/services/environment.service";
import { api_routes } from '@app/consts';
import { Article, ArticleSubmit, ArticleVerbose, EmployeeArticle, EmployeeArticleVerbose } from '../models/article.model';

const headers = new HttpHeaders({
  'Content-Type': 'application/json',
});

@Injectable({
  providedIn: 'root'
})
export class ArticlesService {

  constructor(private http: HttpClient, private envService: EnvironmentService) { }

  getArticles(take?: number, skip?: number, filterString?: string, sort?: string): Observable<PagedData<Article>> {
    let params = new HttpParams();
    take ? params = params.append('take', take) : null;
    skip ? params = params.append('skip', skip) : null;
    filterString ? params = params.append('Filter', filterString) : null;
    sort ? params = params.append('sort', sort) : null;

    return this.http
      .get<any>(`${this.envService.env.apiUrl}${api_routes.ADMIN}/${api_routes.ARTICLES}`, {
        headers: headers,
        params: params
      })
  }

  getArticle(id: string): Observable<ArticleVerbose> {
    return this.http
      .get<any>(`${this.envService.env.apiUrl}${api_routes.ADMIN}/${api_routes.ARTICLES}/${id}`, {
        headers: headers
      })
  }

  deleteArticle(id: string): Observable<any> {
    return this.http
      .delete<any>(`${this.envService.env.apiUrl}${api_routes.ADMIN}/${api_routes.ARTICLES}/${id}`, {
        headers: headers
      })
  }

  createArticle(articleSubmit: ArticleSubmit): Observable<any> {
    return this.http
      .post<any>(`${this.envService.env.apiUrl}${api_routes.ADMIN}/${api_routes.ARTICLES}`, articleSubmit, {
        headers: headers
      });
  }

  editArticle(id: string, articleSubmit: ArticleSubmit): Observable<any> {
    return this.http
      .put<any>(`${this.envService.env.apiUrl}${api_routes.ADMIN}/${api_routes.ARTICLES}/${id}`, articleSubmit, {
        headers: headers
      });
  }

  addArticleFile(articleId: string, file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.http
      .post<any>(`${this.envService.env.apiUrl}${api_routes.ADMIN}/${api_routes.ARTICLES}/${articleId}/${api_routes.FILES}`, formData)
  }

  deleteArticleFile(articleId: string, fileId: string): Observable<any> {
    return this.http
      .delete<any>(`${this.envService.env.apiUrl}${api_routes.ADMIN}/${api_routes.ARTICLES}/${articleId}/${api_routes.FILES}/${fileId}`, {
        headers: headers
      })
  }

  getArticleRegions(take?: number, skip?: number, filterString?: string, sort?: string): Observable<PagedData<{ id: string; name: string }>> {
    let params = new HttpParams();
    take ? params = params.append('take', take) : null;
    skip ? params = params.append('skip', skip) : null;
    filterString ? params = params.append('Filter', filterString) : null;
    sort ? params = params.append('sort', sort) : null;

    return this.http
      .get<any>(`${this.envService.env.apiUrl}${api_routes.ARTICLES}/${api_routes.REGIONS}`, {
        headers: headers,
        params: params
      })
  }

  getEmployeeArticles(region?: string, take?: number, skip?: number, filterString?: string, sort?: string): Observable<PagedData<EmployeeArticle>> {
    let params = new HttpParams();
    region ? params = params.append('region', region) : null;
    take ? params = params.append('take', take) : null;
    skip ? params = params.append('skip', skip) : null;
    filterString ? params = params.append('Filter', filterString) : null;
    sort ? params = params.append('sort', sort) : null;

    return this.http
      .get<any>(`${this.envService.env.apiUrl}${api_routes.ARTICLES}`, {
        headers: headers,
        params: params
      })

    // Mocked data for testing without backend
    let mockedData = new Array<EmployeeArticle>();
    for (let i = 1; i <= (take || 10); i++) {
      const index = (skip || 0) + i;
      mockedData.push({
        id: `article-${index}`,
        title: `Sample Article Title ${index}`,
        splashText: `This is a splash text for article ${index}.`,
        titleImage: {
          id: `image-${index}`,
          name: `title-image-${index}.jpg`,
          contentType: 'image/jpeg',
          fileLength: 204800
        },
        showImageOnSplashPage: true,
        views: Math.floor(Math.random() * 1000),
        createdOn: new Date().toISOString(),
        lastEditedOn: new Date().toISOString(),
        lastViewedOn: new Date().toISOString()
      });
    }

    return of({
      skip: skip || 0,
      take: take || 10,
      totalPages: Math.ceil(100 / (take || 10)),
      total: 100,
      data: mockedData
      });
  }

  getEmployeeArticle(id: string): Observable<EmployeeArticleVerbose> {
    return this.http
      .get<any>(`${this.envService.env.apiUrl}${api_routes.ARTICLES}/${id}`, {
        headers: headers
      })
      
    return(
      of<EmployeeArticleVerbose>({
      id: 'article-1',
      title: 'Sample Article Title 1',
      content: `
        <p>Dear Team,</p>
        <p>We are excited to announce some important updates regarding our company initiatives for Q1 2024.</p>
        
        <h5>New Office Location</h5>
        <p>Starting next month, we will be opening a new office in downtown Seattle. This expansion reflects our continued growth and commitment to providing better facilities for our team.</p>
        
        <h5>Updated Benefits Package</h5>
        <ul>
        <li>Enhanced health insurance coverage</li>
        <li>Additional paid time off</li>
        <li>Professional development stipend increased to $2,000 annually</li>
        <li>Flexible work arrangements</li>
        </ul>
        
        <h5>Upcoming Events</h5>
        <p>Mark your calendars for the following events:</p>
        <ul>
        <li><strong>Team Building Day:</strong> March 15, 2024</li>
        <li><strong>Annual Company Conference:</strong> April 10-12, 2024</li>
        <li><strong>Q1 Town Hall:</strong> March 30, 2024</li>
        </ul>
        
        <p>If you have any questions, please reach out to HR or your direct manager.</p>
        <p>Best regards,<br>Management Team</p>
      `,
      titleImage: {
        id: 'image-1',
        name: 'title-image-1.jpg',
        contentType: 'image/jpeg',
        fileLength: 204800
      },
      files: [{
        id: 'image-1',
        name: 'title-image-1.jpg',
        contentType: 'image/jpeg',
        fileLength: 204800
      }],
      views: 123,
      createdOn: new Date().toISOString(),
      lastEditedOn: new Date().toISOString(),
      lastViewedOn: new Date().toISOString()
      })
    )

    
  }

  getEmployeeArticleFile(articleId: string, fileId: string): any {
    return this.http.get(`${this.envService.env.apiUrl}${api_routes.ARTICLES}/${articleId}/${api_routes.FILES}/${fileId}`, { responseType: 'text' })
  }
}
